'use client';
import React from 'react';
import MuiGrid from '@new-components/atoms/MuiGrid';
import MuiTypography from '@new-components/atoms/MuiTypography';
import MuiToggleButton from '@new-components/atoms/MuiToggleButton';
import MuiToggleButtonGroup from '@new-components/atoms/MuiToggleButtonGroup';
import MuiBox from '@new-components/atoms/MuiBox';
import {IconMap} from '@new-components/templates/SearchBox/useSearchState';

export interface PropertyTypeOption {
  label: string;
  value: string;
  icon: React.ReactElement;
}

interface PropertyTypeSelectorProps {
  options: PropertyTypeOption[];
  title: string;
  subTitle: string;
  selectedTypes: string[];
  setSelectedTypes: (value: string[]) => void;
}

const PropertyTypeSelector = (props: PropertyTypeSelectorProps) => {
  const options = React.useMemo(() => {
    return [
      {
        label: 'Any',
        value: 'Any',
        icon: IconMap.default,
      },
      ,
      ...props.options,
    ];
  }, [props.options]);
  const handleSelectionChange = (
    event: React.MouseEvent<HTMLElement>,
    newSelection: string[]
  ) => {
    if (
      newSelection.length === 0 ||
      (newSelection.includes('Any') && newSelection.length === 1) ||
      (newSelection.includes('Any') && props.selectedTypes.length > 0)
    ) {
      props.setSelectedTypes([]);
    } else if (newSelection.includes('Any') && newSelection.length > 1) {
      props.setSelectedTypes(newSelection.filter((item) => item !== 'Any'));
    } else {
      props.setSelectedTypes(newSelection);
    }
  };

  const selectedValue = () => {
    if (props.selectedTypes.length === 0) return ['Any'];
    return props.selectedTypes;
  };

  return (
    <MuiBox>
      <MuiBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          paddingBottom: '16px',
        }}>
        <MuiTypography
          sx={{
            height: '24px',
            fontSize: '17px',
            fontWeight: 500,
            letterSpacing: '-0.4px',
            color: '#000000',
          }}>
          {props.title}
        </MuiTypography>
        <MuiTypography
          sx={{
            height: '16px',
            fontSize: '13px',
            fontWeight: 400,
            letterSpacing: '-0.4px',
            color: '#82807EB2',
          }}>
          {props.subTitle}
        </MuiTypography>
      </MuiBox>
      <MuiToggleButtonGroup
        value={selectedValue()}
        onChange={handleSelectionChange}
        sx={{width: '100%'}}>
        <MuiGrid container spacing={1} columns={{xs: 2, sm: 2, md: 3}}>
          {options.map(
            (option) =>
              option && (
                <MuiGrid item xs={1} key={option?.value}>
                  <MuiToggleButton
                    value={option.value}
                    aria-label={`Select ${option.label}`}
                    sx={{
                      'display': 'flex',
                      'flexDirection': 'column',
                      'alignItems': 'baseline',
                      'justifyContent': 'center',
                      'textTransform': 'none',
                      'width': '100%',
                      'borderRadius': 2,
                      'border': '1px solid #82807EB2',
                      'padding': '8px clamp(12px, 1.5vw, 13px)',
                      'gap': '12px',
                      '&.Mui-selected': {
                        backgroundColor: '#EDECE8',
                      },
                    }}>
                    <MuiBox component={'span'} sx={{display: 'flex'}}>
                      {option.icon}
                    </MuiBox>
                    <MuiTypography
                      sx={{
                        maxHeight: '19px',
                        fontSize: 'clamp(13px, 2vw, 15px)',
                        textAlign: 'left',
                        fontWeight: '400',
                        letterSpacing: '-0.4px',
                        color: '#2D2D2D',
                      }}
                      variant='body2'>
                      {option.label}
                    </MuiTypography>
                  </MuiToggleButton>
                </MuiGrid>
              )
          )}
        </MuiGrid>
      </MuiToggleButtonGroup>
    </MuiBox>
  );
};
export default PropertyTypeSelector;
